// ============================================================================
// Componente: FiscalReducaoBeneficio.js
// Objetivo: Relatório "Redução do Benefício (CST 06)" do módulo Fiscal.
//           Lista, na base Sovos (dtbSovosItens), as vendas das NCMs com
//           benefício fiscal de PIS (alíquota zero, CST 06) — leite/derivados
//           (0404 9000) e águas/bebidas (2202 1000 / 2201 1000) — para conferir
//           o que está saindo com a redução aplicada.
//
// Backend: GET /api/fiscal/reducao-beneficio (JSON p/ a tela; ?formato=xlsx = Excel
//          com aba Consolidado (líquido por loja) + aba Detalhado). Dados AGRUPADOS
//          por loja (CNPJ emitente) + NCM + dia.
//
// VOLUME: as NCMs/CST sozinhas dão ~300 mil linhas/mês. A tela tem teto de segurança
//         (avisa quando trunca); o dado completo sai no Excel. Renderizada DENTRO do
//         hub de Relatórios Fiscais (embedded) — não desenha casco próprio.
// ============================================================================

import React, { useState, useCallback } from 'react';
import { Search, Download, AlertTriangle, ArrowLeft, Info, Percent } from 'lucide-react';

const COR = '#7c3aed';        // roxo do Fiscal
const COR_ESCURA = '#6d28d9';
const ITENS_POR_PAGINA = 50;

// Sem "Todas": o relatório é SEMPRE por uma empresa (as 4 juntas estouram o teto da tela).
const EMPRESAS = [
    { value: 'Hunger', label: 'Hunger' },
    { value: 'PG', label: 'P&G' },
    { value: 'PM', label: 'P&M' },
    { value: 'GC', label: 'GC Food' },
];

// Colunas exibidas (ordem fixa). num = numérica (alinhada à direita); center = centralizada.
const COLUNAS = [
    { key: 'DT_EMISSAO', label: 'Data Emissão' },
    { key: 'LOJA', label: 'Loja', center: true },
    { key: 'CD_NCM', label: 'NCM', center: true },
    { key: 'CD_CST_PIS', label: 'CST PIS', center: true },
    { key: 'CD_CST_COFINS', label: 'CST COFINS', center: true },
    { key: 'VL_LIQUIDO', label: 'Vl. Líquido', num: true },
];

const fmtBRL = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? (v ?? '') : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Datas padrão: do 1º dia do mês corrente até hoje (período curto = consulta leve).
const isoHoje = () => new Date().toISOString().slice(0, 10);
const isoInicioMes = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const FiscalReducaoBeneficio = ({ onVoltar }) => {
    const [empresa, setEmpresa] = useState('Hunger');
    const [dataInicio, setDataInicio] = useState(isoInicioMes());
    const [dataFim, setDataFim] = useState(isoHoje());

    const [linhas, setLinhas] = useState([]);
    const [totais, setTotais] = useState(null);
    const [truncado, setTruncado] = useState(false);
    const [limite, setLimite] = useState(0);
    const [loading, setLoading] = useState(false);
    const [baixandoExcel, setBaixandoExcel] = useState(false);
    const [erro, setErro] = useState('');
    const [pesquisado, setPesquisado] = useState(false);
    const [paginaAtual, setPaginaAtual] = useState(1);

    const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

    const montarQuery = () => {
        const qs = new URLSearchParams();
        qs.set('dataInicio', dataInicio);
        qs.set('dataFim', dataFim);
        if (empresa) qs.set('empresa', empresa);
        return qs.toString();
    };

    const buscar = useCallback(async () => {
        if (!dataInicio || !dataFim) { setErro('Informe o período (data início e data fim).'); return; }
        if (dataFim < dataInicio) { setErro('A data final não pode ser anterior à inicial.'); return; }

        setLoading(true); setErro(''); setPesquisado(true); setPaginaAtual(1);
        try {
            const r = await fetch(`/api/fiscal/reducao-beneficio?${montarQuery()}`, { headers: auth() });
            if (!r.ok) {
                const txt = await r.text();
                throw new Error(`HTTP ${r.status} — ${txt}`);
            }
            const data = await r.json();
            setLinhas(Array.isArray(data.linhas) ? data.linhas : []);
            setTotais(data.totais || null);
            setTruncado(!!data.truncado);
            setLimite(data.limite || 0);
        } catch (e) {
            setErro(e.message);
            setLinhas([]); setTotais(null); setTruncado(false);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresa, dataInicio, dataFim]);

    // Exporta o Excel (.xlsx): aba Consolidado (líquido por loja) + aba Detalhado.
    const exportarExcel = async () => {
        setBaixandoExcel(true);
        try {
            const r = await fetch(`/api/fiscal/reducao-beneficio?${montarQuery()}&formato=xlsx`, { headers: auth() });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Reducao_Beneficio_CST06_${dataInicio}_a_${dataFim}_${empresa}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErro('Erro ao exportar Excel: ' + e.message);
        } finally {
            setBaixandoExcel(false);
        }
    };

    const totalPaginas = Math.max(1, Math.ceil(linhas.length / ITENS_POR_PAGINA));
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    const pagina = linhas.slice(inicio, inicio + ITENS_POR_PAGINA);

    return (
        <div>
            {/* Cabeçalho + voltar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                {onVoltar && (
                    <button onClick={onVoltar} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                        color: '#475569', fontWeight: 700, cursor: 'pointer',
                    }}>
                        <ArrowLeft size={16} /> Voltar
                    </button>
                )}
                <Percent size={24} color={COR} />
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Redução do Benefício (CST 06)</h3>
            </div>

            {/* Explicação */}
            <div style={{ background: '#f5f3ff', borderLeft: `4px solid ${COR}`, padding: '14px 18px', borderRadius: 8, marginBottom: 20 }}>
                <p style={{ margin: 0, color: '#4c1d95', fontSize: 13.5, lineHeight: 1.6 }}>
                    Lista as vendas das NCMs com <strong>benefício fiscal de PIS</strong> (alíquota zero, <strong>CST 06</strong>) na base Sovos —
                    leite e derivados (<strong>04049000</strong>) e águas/bebidas (<strong>22021000</strong> e <strong>22011000</strong>) — para conferir o benefício aplicado.
                    <br />Os dados vêm <strong>agrupados por loja, NCM e dia</strong>; o <em>Vl. Líquido</em> (Total − Desconto) é a soma de todas as notas daquela loja/NCM no dia.
                    <br />O relatório é sempre <strong>por empresa</strong> (escolha no filtro). Pensado para a <strong>extração mensal</strong>.
                </p>
            </div>

            {/* Filtros */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr)) auto auto', gap: 12, alignItems: 'end' }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Empresa</label>
                        <select value={empresa} onChange={(e) => setEmpresa(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }}>
                            {EMPRESAS.map((emp) => <option key={emp.value} value={emp.value}>{emp.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Data início (emissão)</label>
                        <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Data fim (emissão)</label>
                        <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
                            style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                    </div>
                    <button onClick={buscar} disabled={loading} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '10px 16px', background: loading ? '#94a3b8' : COR_ESCURA, color: '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 800, cursor: loading ? 'wait' : 'pointer',
                    }}>
                        <Search size={16} /> {loading ? 'Buscando…' : 'Buscar'}
                    </button>
                    <button onClick={exportarExcel} disabled={loading || baixandoExcel} title="Exporta um Excel com aba Consolidado (líquido por loja) + aba Detalhado" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '10px 16px', background: (loading || baixandoExcel) ? '#e2e8f0' : '#059669',
                        color: (loading || baixandoExcel) ? '#64748b' : '#fff',
                        border: 'none', borderRadius: 8, fontWeight: 800,
                        cursor: (loading || baixandoExcel) ? 'not-allowed' : 'pointer',
                    }}>
                        <Download size={16} /> {baixandoExcel ? 'Gerando Excel…' : 'Exportar Excel'}
                    </button>
                </div>
                <p style={{ margin: '12px 0 0', fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Info size={13} /> A base Sovos é enorme — quanto menor o período, mais rápida a consulta. O Excel traz o dado completo, sem o limite da tela.
                </p>
            </div>

            {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}

            {/* Aviso de resultado truncado na tela */}
            {truncado && (
                <div style={{ padding: 12, background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertTriangle size={18} /> Mais de {limite.toLocaleString('pt-BR')} linhas no período — a tela mostra só as primeiras {limite.toLocaleString('pt-BR')} (e os totais abaixo são parciais). Estreite o período ou baixe o <strong>Excel</strong> para o dado completo.
                </div>
            )}

            {/* Cards de totais: linhas + líquido geral */}
            {linhas.length > 0 && totais && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: '1 1 200px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: '#6d28d9', fontWeight: 700 }}>Linhas{truncado ? ' (parcial)' : ''}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{totais.linhas.toLocaleString('pt-BR')}</div>
                    </div>
                    <div style={{ flex: '1 1 200px', background: COR, borderRadius: 10, padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: '#ede9fe', fontWeight: 700 }}>Vl. Líquido{truncado ? ' (parcial)' : ''}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{fmtBRL(totais.somaLiquido)}</div>
                    </div>
                </div>
            )}

            {/* Cards de líquido por loja (uma empresa pode ter várias lojas) */}
            {linhas.length > 0 && totais && Array.isArray(totais.porLoja) && totais.porLoja.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: COR_ESCURA, marginBottom: 8 }}>
                        Vl. Líquido por loja{truncado ? ' (parcial)' : ''}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {totais.porLoja.map((l) => (
                            <div key={l.loja} style={{ flex: '1 1 200px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '12px 16px' }}>
                                <div style={{ fontSize: 12, color: '#6d28d9', fontWeight: 700 }}>{l.loja}</div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{fmtBRL(l.somaLiquido)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Resumo / vazio */}
            {pesquisado && !loading && !erro && linhas.length === 0 && (
                <div style={{ padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, color: '#475569', fontWeight: 600 }}>
                    Nenhuma venda encontrada para os filtros do período.
                </div>
            )}
            {linhas.length > 0 && (
                <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
                    <strong>{linhas.length.toLocaleString('pt-BR')}</strong> linha(s) na tela.
                    {linhas.length > ITENS_POR_PAGINA && ` · Página ${paginaAtual} de ${totalPaginas}`}
                </div>
            )}

            {/* Tabela */}
            {linhas.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead style={{ background: '#f5f3ff', position: 'sticky', top: 0 }}>
                            <tr>
                                {COLUNAS.map((c) => (
                                    <th key={c.key} style={{
                                        padding: 10, textAlign: c.num ? 'right' : (c.center ? 'center' : 'left'),
                                        fontWeight: 800, color: COR_ESCURA,
                                        borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
                                    }}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pagina.map((row, i) => (
                                <tr key={inicio + i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    {COLUNAS.map((c) => (
                                        <td key={c.key} style={{
                                            padding: 10,
                                            color: '#0f172a',
                                            textAlign: c.num ? 'right' : (c.center ? 'center' : 'left'),
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {c.num ? fmtNum(row[c.key]) : (row[c.key] ?? '')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Paginação */}
            {linhas.length > ITENS_POR_PAGINA && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    <button onClick={() => setPaginaAtual(1)} disabled={paginaAtual === 1} style={btnPaginacao(paginaAtual === 1)}>« Primeira</button>
                    <button onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))} disabled={paginaAtual === 1} style={btnPaginacao(paginaAtual === 1)}>Anterior</button>
                    <span style={{ padding: '8px 14px', background: COR, color: '#fff', borderRadius: 6, fontWeight: 700, fontSize: 13 }}>
                        {paginaAtual} / {totalPaginas}
                    </span>
                    <button onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))} disabled={paginaAtual === totalPaginas} style={btnPaginacao(paginaAtual === totalPaginas)}>Próxima</button>
                    <button onClick={() => setPaginaAtual(totalPaginas)} disabled={paginaAtual === totalPaginas} style={btnPaginacao(paginaAtual === totalPaginas)}>Última »</button>
                </div>
            )}
        </div>
    );
};

const btnPaginacao = (desabilitado) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '8px 12px',
    background: desabilitado ? '#f1f5f9' : '#fff',
    color: desabilitado ? '#94a3b8' : '#475569',
    border: '1px solid #cbd5e1', borderRadius: 6,
    fontWeight: 700, fontSize: 13,
    cursor: desabilitado ? 'not-allowed' : 'pointer',
});

export default FiscalReducaoBeneficio;
